// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — a Khipu played on a timeline (docs/AYNI_XR_STUDIO_MOTION_PLAN.md).
 *
 * Each step is a **stop**: the camera arrives (its `transition`: cut, fly,
 * glide or along a path, with a feel), then the stop **holds** for `hold`
 * seconds, while the camera may orbit or push in. The data changes on the way
 * in (`transition.data`): at once half-way, dissolving, sweeping, through the
 * plain Earth, or running fast between two dates of the same dataset.
 *
 *   stop 1: hold │ stop 2: arrive · hold │ stop 3: arrive · hold │ …
 *
 * Pure: the same timeline drives the render (frame by frame), the Studio's
 * preview and the Khipu player's live moves, so a video and a lesson move
 * alike. The globe's zoom stays below 10 (owner rule).
 */

import type { KhipuDataMove, KhipuEffects, KhipuFeel, KhipuMove, KhipuPlace, KhipuStep, KhipuTransition } from '../khipu'

export interface FilmView {
  lat: number
  lon: number
  zoom: number
  bearing: number
  pitch: number
}

/** A transition with every choice made (defaults filled in). */
export interface FilmTransition {
  move: KhipuMove
  seconds: number
  feel: KhipuFeel
  path: KhipuPlace[]
  during: 'still' | 'orbit' | 'pushIn' | 'pullOut'
  amount: number
  data: KhipuDataMove
  wipe: 'meridian' | 'terminator'
}

export interface FilmStop {
  index: number
  stepId: string
  /** The step's dataset key (`datasets[].key`), or null for none. */
  dataset: string | null
  view: FilmView
  /** Epoch ms of the data at the stop, or null to leave the dataset where it is. */
  atMs: number | null
  playing: boolean
  rate: number
  hold: number
  transition: FilmTransition
  effects: KhipuEffects
  /** AYNI (multi-globe plan): globes on screen during this stop (1, 2 or 4), as the Khipu player lays them out. */
  globes: 1 | 2 | 4
  /** When the arrival starts, when the hold starts, when the stop ends (seconds). */
  start: number
  holdStart: number
  end: number
}

export interface FilmPlan {
  stops: FilmStop[]
  seconds: number
}

/** Which stop's data is on the globe, at which date, and what blends into it (data moves). */
export interface FilmData {
  stop: number
  atMs: number | null
  /** Seconds of the dataset's video played on from `atMs` (a playing stop). */
  playSeconds: number
  move: KhipuDataMove
  /** Crossfade and wipe: the incoming stop drawn over the main one, 0 → 1. */
  incoming: { stop: number; atMs: number | null; mix: number } | null
  /** Through the basemap: the main dataset's opacity. */
  opacity: number
}

export interface FilmSample {
  /** The stop being arrived at or held. */
  stop: number
  phase: 'arrive' | 'hold'
  /** Seconds into the phase, and 0–1 of it. */
  local: number
  progress: number
  view: FilmView
  data: FilmData
}

export const DEFAULT_HOLD = 4
const ZOOM_MAX = 9.99
const ZOOM_MIN = 0
const DEG = Math.PI / 180

export const EASE: Record<KhipuFeel, (x: number) => number> = {
  gentle: (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2),
  snappy: (x) => 1 - Math.pow(1 - x, 4),
  linear: (x) => x,
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
const clamp01 = (v: number) => clamp(v, 0, 1)
const wrapLon = (lon: number) => ((((lon + 180) % 360) + 360) % 360) - 180
const angleDelta = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180

function toVec(p: KhipuPlace): [number, number, number] {
  const la = p.lat * DEG, lo = p.lon * DEG
  return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)]
}
function fromVec(v: [number, number, number]): KhipuPlace {
  const r = Math.hypot(v[0], v[1], v[2]) || 1
  return { lat: Math.asin(clamp(v[2] / r, -1, 1)) / DEG, lon: Math.atan2(v[1], v[0]) / DEG }
}

/** Great-circle angle between two places, in degrees. */
export function arcDegrees(a: KhipuPlace, b: KhipuPlace): number {
  const [x1, y1, z1] = toVec(a), [x2, y2, z2] = toVec(b)
  return Math.acos(clamp(x1 * x2 + y1 * y2 + z1 * z2, -1, 1)) / DEG
}

/** The point a fraction `f` of the way along the great circle from `a` to `b`. */
export function slerpPlace(a: KhipuPlace, b: KhipuPlace, f: number): KhipuPlace {
  const va = toVec(a), vb = toVec(b)
  const dot = clamp(va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2], -1, 1)
  const omega = Math.acos(dot)
  if (omega < 1e-9) return { lat: a.lat, lon: a.lon }
  if (Math.PI - omega < 1e-6) {
    // Antipodes: no single great circle; go the short way in longitude.
    return { lat: a.lat + (b.lat - a.lat) * f, lon: wrapLon(a.lon + angleDelta(a.lon, b.lon) * f) }
  }
  const s = Math.sin(omega)
  const ka = Math.sin((1 - f) * omega) / s, kb = Math.sin(f * omega) / s
  return fromVec([va[0] * ka + vb[0] * kb, va[1] * ka + vb[1] * kb, va[2] * ka + vb[2] * kb])
}

/** The place a fraction `f` along a route of places (by great-circle length). */
export function alongRoute(points: KhipuPlace[], f: number): KhipuPlace {
  if (points.length === 1) return points[0]
  const legs = points.slice(1).map((p, i) => arcDegrees(points[i], p))
  const total = legs.reduce((s, d) => s + d, 0)
  if (total < 1e-9) return points[0]
  let left = clamp01(f) * total
  for (let i = 0; i < legs.length; i++) {
    if (left <= legs[i] || i === legs.length - 1) return slerpPlace(points[i], points[i + 1], legs[i] ? clamp01(left / legs[i]) : 1)
    left -= legs[i]
  }
  return points[points.length - 1]
}

/** How far the camera rises on a fly, in zoom levels: more for longer trips. */
export function flyHop(from: FilmView, to: FilmView): number {
  const d = arcDegrees(from, to)
  return clamp(1.25 * Math.log2(1 + d / 12), 0, 4)
}

/** A move's natural length when the step does not say. */
export function autoSeconds(move: KhipuMove, data: KhipuDataMove, from: FilmView, to: FilmView, path: KhipuPlace[]): number {
  const d = arcDegrees(from, to)
  const dataSeconds = data === 'cut' ? 0 : data === 'timeRun' ? 6 : 1.5
  if (move === 'cut') return dataSeconds
  if (d < 0.05 && Math.abs(to.zoom - from.zoom) < 0.05) return dataSeconds
  if (move === 'glide') return Math.max(dataSeconds, clamp(1.5 + d / 40 + Math.abs(to.zoom - from.zoom) * 0.4, 1.5, 5))
  if (move === 'path') {
    const route = [from, ...path, to]
    const len = route.slice(1).reduce((s, p, i) => s + arcDegrees(route[i], p), 0)
    return Math.max(dataSeconds, clamp(2 + len / 30, 2, 12))
  }
  return Math.max(dataSeconds, clamp(1.8 + d / 35 + Math.abs(to.zoom - from.zoom) * 0.3, 1.8, 6))
}

/** A step's transition with every choice made. The first stop never moves in. */
export function resolveTransition(t: KhipuTransition | undefined, from: FilmView | null, to: FilmView, first: boolean): FilmTransition {
  const move: KhipuMove = first ? 'cut' : t?.move ?? 'fly'
  const data: KhipuDataMove = first ? 'cut' : t?.data ?? 'cut'
  const path = t?.path ?? []
  const during = t?.during ?? 'still'
  const amount = typeof t?.amount === 'number' ? t.amount : during === 'orbit' ? 45 : during === 'still' ? 0 : 0.7
  const seconds = first ? 0 : typeof t?.seconds === 'number' ? t.seconds : autoSeconds(move, data, from ?? to, to, path)
  return { move, seconds, feel: t?.feel ?? 'gentle', path, during, amount, data, wipe: t?.wipe ?? 'meridian' }
}

/** The camera `progress` (0–1) of the way through an arrival. */
export function arrivalView(from: FilmView, to: FilmView, tr: FilmTransition, progress: number): FilmView {
  // A finished arrival lands exactly on the stop (no floating-point remainder of the hop).
  if (tr.move === 'cut' || tr.seconds <= 0 || progress >= 1) return { ...to }
  const e = EASE[tr.feel](clamp01(progress))
  const center = tr.move === 'path' ? alongRoute([from, ...tr.path, to], e) : slerpPlace(from, to, e)
  let zoom = from.zoom + (to.zoom - from.zoom) * e
  if (tr.move === 'fly') zoom -= flyHop(from, to) * Math.sin(Math.PI * e)
  return {
    lat: center.lat,
    lon: wrapLon(center.lon),
    zoom: clamp(zoom, ZOOM_MIN, ZOOM_MAX),
    bearing: ((from.bearing + angleDelta(from.bearing, to.bearing) * e) % 360 + 360) % 360,
    pitch: from.pitch + (to.pitch - from.pitch) * e,
  }
}

/** The camera `progress` (0–1) of the way through a hold: still, orbiting or pushing. */
export function holdView(view: FilmView, tr: FilmTransition, progress: number): FilmView {
  const p = clamp01(progress)
  if (tr.during === 'orbit') return { ...view, bearing: ((view.bearing + tr.amount * p) % 360 + 360) % 360 }
  if (tr.during === 'pushIn' || tr.during === 'pullOut') {
    const e = (1 - Math.cos(Math.PI * p)) / 2
    const dz = tr.during === 'pushIn' ? tr.amount : -tr.amount
    return { ...view, zoom: clamp(view.zoom + dz * e, ZOOM_MIN, ZOOM_MAX) }
  }
  return { ...view }
}

/** The camera at the end of a stop's hold (where the next arrival starts). */
export function holdEndView(stop: FilmStop): FilmView {
  return holdView(stop.view, stop.transition, 1)
}

function stepView(step: KhipuStep, fallback: FilmView | null): FilmView | null {
  const v = step.view
  if (!v) return fallback
  return { lat: v.lat, lon: v.lon, zoom: clamp(v.zoom, ZOOM_MIN, ZOOM_MAX), bearing: v.bearing ?? 0, pitch: v.pitch ?? 0 }
}

/**
 * The globes a step shows, by the Khipu player's rule (`khipuPlayback.globesOf`):
 * its layout, else as many as it names; none named is one globe (the render
 * goes back to one for a stop without them).
 */
export function stepGlobes(step: KhipuStep): 1 | 2 | 4 {
  const layout = step.layout
  if (layout === '2h' || layout === '2v') return 2
  if (layout === '4') return 4
  if (layout === '1') return 1
  const others = Array.isArray(step.globes) ? Math.min(3, step.globes.length) : 0
  return others === 0 ? 1 : others === 1 ? 2 : 4
}

/** A step's hold: its own, else its timer, else the default. */
export function stepHold(step: KhipuStep): number {
  if (typeof step.hold === 'number' && step.hold > 0) return step.hold
  if (step.advance?.mode === 'timer' && typeof step.advance.seconds === 'number') return step.advance.seconds
  return DEFAULT_HOLD
}

/**
 * The timeline of a Khipu's steps, or null when no step has a camera.
 * `resolveRelative` turns a step's "first" or "latest" date into epoch ms
 * (the render knows the dataset's axis); without it such a date is left open.
 */
export function planFilm(steps: KhipuStep[], resolveRelative?: (step: KhipuStep, edge: 'first' | 'latest') => number | null): FilmPlan | null {
  const stops: FilmStop[] = []
  let prevView: FilmView | null = null
  let prevDataset: string | null = null
  let prevAt: number | null = null
  let clock = 0
  for (const step of steps) {
    const view = stepView(step, prevView)
    if (!view) continue
    const dataset: string | null = step.dataset === undefined ? (stops.length ? prevDataset : null) : step.dataset
    let atMs: number | null = null
    if (step.time?.at && !Number.isNaN(Date.parse(step.time.at))) atMs = Date.parse(step.time.at)
    else if (step.time?.relative) atMs = resolveRelative?.(step, step.time.relative) ?? null
    else if (dataset === prevDataset) atMs = prevAt
    const first = stops.length === 0
    const transition = resolveTransition(step.transition, prevView, view, first)
    // A run between dates only makes sense within one dataset with both dates known.
    if (transition.data === 'timeRun' && (dataset !== prevDataset || atMs === null || prevAt === null)) transition.data = 'cut'
    const hold = stepHold(step)
    const start = clock
    const holdStart = start + transition.seconds
    const end = holdStart + hold
    stops.push({
      index: stops.length, stepId: step.id, dataset, view, atMs,
      playing: step.time?.playing === true, rate: typeof step.time?.rate === 'number' ? step.time.rate : 1,
      hold, transition, effects: step.effects ?? {}, globes: stepGlobes(step), start, holdStart, end,
    })
    clock = end
    prevView = holdView(view, transition, 1)
    prevDataset = dataset
    prevAt = atMs
  }
  return stops.length ? { stops, seconds: clock } : null
}

/** Where a stop's data stands at the end of its hold (a playing stop has run on). */
function endOfHold(stop: FilmStop): { atMs: number | null; playSeconds: number } {
  return { atMs: stop.atMs, playSeconds: stop.playing ? stop.hold * stop.rate : 0 }
}

/** Everything about the film at `t` seconds. */
export function sampleFilm(plan: FilmPlan, t: number): FilmSample {
  const time = clamp(t, 0, plan.seconds)
  let i = plan.stops.findIndex((s) => time < s.end)
  if (i < 0) i = plan.stops.length - 1
  const stop = plan.stops[i]
  const prev = i > 0 ? plan.stops[i - 1] : null
  const tr = stop.transition

  if (time < stop.holdStart && prev) {
    const local = time - stop.start
    const progress = tr.seconds > 0 ? clamp01(local / tr.seconds) : 1
    const view = arrivalView(holdEndView(prev), stop.view, tr, progress)
    const before = endOfHold(prev)
    const base: FilmData = { stop: prev.index, atMs: before.atMs, playSeconds: before.playSeconds, move: tr.data, incoming: null, opacity: 1 }
    let data: FilmData
    switch (tr.data) {
      case 'timeRun':
        data = {
          ...base, stop: stop.index, playSeconds: 0,
          atMs: before.atMs! + (stop.atMs! - before.atMs!) * progress,
        }
        break
      case 'crossfade':
      case 'wipe':
        data = { ...base, incoming: { stop: stop.index, atMs: stop.atMs, mix: progress } }
        break
      case 'basemap':
        data = progress < 0.5
          ? { ...base, opacity: 1 - progress * 2 }
          : { ...base, stop: stop.index, atMs: stop.atMs, playSeconds: 0, opacity: progress * 2 - 1 }
        break
      default:
        data = progress < 0.5 ? base : { ...base, stop: stop.index, atMs: stop.atMs, playSeconds: 0 }
    }
    return { stop: i, phase: 'arrive', local, progress, view, data }
  }
  const local = time - stop.holdStart
  const progress = stop.hold > 0 ? clamp01(local / stop.hold) : 1
  return {
    stop: i, phase: 'hold', local, progress,
    view: holdView(stop.view, tr, progress),
    data: { stop: i, atMs: stop.atMs, playSeconds: stop.playing ? local * stop.rate : 0, move: tr.data, incoming: null, opacity: 1 },
  }
}
